import { throttle } from "throttle-debounce";
import { useEffect, useMemo, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { ChevronsUpDownIcon, XIcon, ChevronDownIcon, ExternalLinkIcon, FilterXIcon } from 'lucide-react';
import { Input } from '@/components/ui/input';
import {
    DropdownMenu,
    DropdownMenuCheckboxItem,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuRadioGroup,
    DropdownMenuRadioItem,
    DropdownMenuSeparator, DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import InlineCode from '@/components/InlineCode';
import { cn } from '@/lib/utils';
import { useEventListener } from "usehooks-ts";
import { Link } from "wouter";
import { HoverTip } from '@/components/ui/tooltip';
import { useAuth } from "@/hooks/auth"
import { HistoryTableSearchType } from "@shared/historyApiTypes";


/**
 * Helpers
 */
export const availableSearchTypes = [
    {
        value: 'actionId',
        label: 'Action ID',
        placeholder: 'XXXX-XXXX',
        description: 'Search actions by their ID.'
    },
    {
        value: 'playerName',
        label: 'FiveM Name',
        placeholder: 'Enter a FiveM player name',
        description: 'Search actions by the FiveM name the player had at the time.'
    },
    {
        value: 'coreName',
        label: 'RP Name',
        placeholder: 'Enter an RP name',
        description: 'Search actions by the RP name the player had at the time.'
    },
    {
        value: 'identifiers',
        label: 'Player IDs',
        placeholder: 'License, Discord, Steam, etc.',
        description: 'Search actions by their player IDs separated by a comma.'
    },
    {
        value: 'reason',
        label: 'Reason',
        placeholder: 'Enter part of the reason to search for',
        description: 'Search actions by their reason contents.'
    },
] as const;

export const SEARCH_ANY_STRING = '!any';

//FIXME: this doesn't require exporting, but HMR doesn't work without it
// eslint-disable-next-line @typescript-eslint/no-explicit-any, react-refresh/only-export-components
export const throttleFunc = throttle(1250, (func: any) => {
    func();
}, { noLeading: true });



/**
 * Component
 */
export type HistorySearchBoxReturnStateType = {
    search: HistoryTableSearchType;
    filterbyType?: string;
    filterbyAdmin?: string;
}

type HistorySearchBoxProps = {
    doSearch: (
        search: HistoryTableSearchType,
        filterbyType: string | undefined,
        filterbyAdmin: string | undefined,
        rememberSearchType: boolean,
    ) => void;
    initialState: HistorySearchBoxReturnStateType & { rememberSearchType: boolean };
    adminStats: {
        name: string;
        actions: number;
    }[];
};

export function HistorySearchBox({ doSearch, initialState, adminStats }: HistorySearchBoxProps) {
    const { authData } = useAuth();
    const inputRef = useRef<HTMLInputElement>(null);
    const [isSearchTypeDropdownOpen, setSearchTypeDropdownOpen] = useState(false);
    const [currSearchType, setCurrSearchType] = useState<string>(initialState.search.type);
    const [hasSearchText, setHasSearchText] = useState(!!initialState.search.value);
    const [typeFilter, setTypeFilter] = useState(initialState.filterbyType);
    const [adminNameFilter, setAdminNameFilter] = useState(initialState.filterbyAdmin);
    const [rememberSearchType, setRememberSearchType] = useState(initialState.rememberSearchType);

    const updateSearch = () => {
        if (!inputRef.current) return;
        const searchValue = inputRef.current.value.trim();
        const effectiveTypeFilter = typeFilter !== SEARCH_ANY_STRING ? typeFilter : undefined;
        const effectiveAdminNameFilter = adminNameFilter !== SEARCH_ANY_STRING ? adminNameFilter : undefined;
        doSearch(
            { value: searchValue, type: currSearchType },
            effectiveTypeFilter,
            effectiveAdminNameFilter,
            rememberSearchType,
        );
    }

    //Call onSearch when params change
    useEffect(() => {
        updateSearch();
    }, [currSearchType, typeFilter, adminNameFilter, rememberSearchType]);

    //Input handlers
    const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            throttleFunc.cancel({ upcomingOnly: true });
            updateSearch();
        } else if (e.key === 'Escape') {
            inputRef.current!.value = '';
            throttleFunc(updateSearch);
            setHasSearchText(false);
        } else {
            throttleFunc(updateSearch);
            setHasSearchText(true);
        }
    };

    const clearSearchBtn = () => {
        inputRef.current!.value = '';
        throttleFunc.cancel({ upcomingOnly: true });
        updateSearch();
        setHasSearchText(false);
    };

    const isTypeFiltered = !!typeFilter && typeFilter !== SEARCH_ANY_STRING;
    const isAdminFiltered = !!adminNameFilter && adminNameFilter !== SEARCH_ANY_STRING;
    const hasAnyFilter = hasSearchText || isTypeFiltered || isAdminFiltered;
    const clearAllFilters = () => {
        inputRef.current!.value = '';
        setHasSearchText(false);
        throttleFunc.cancel({ upcomingOnly: true });
        setTypeFilter(SEARCH_ANY_STRING);
        setAdminNameFilter(SEARCH_ANY_STRING);
        if (!isTypeFiltered && !isAdminFiltered) updateSearch();
    };

    //Search hotkey
    useEventListener('keydown', (e: KeyboardEvent) => {
        if (e.code === 'KeyF' && (e.ctrlKey || e.metaKey)) {
            inputRef.current?.focus();
            e.preventDefault();
        }
    });

    //It's render time
    const selectedSearchType = availableSearchTypes.find((type) => type.value === currSearchType);
    if (!selectedSearchType) throw new Error(`Invalid search type: ${currSearchType}`);
    if (!authData) throw new Error(`authData is not available`);
    const filteredAdmins = useMemo(() => {
        return adminStats.filter((admin) => admin.name !== authData.name)
    }, [adminStats, authData.name]);
    const selfActionCount = useMemo(() => {
        return adminStats.find((admin) => admin.name === authData.name)?.actions || 0;
    }, [adminStats, authData.name]);
    const typeFilterOptions = [
        { value: SEARCH_ANY_STRING, label: 'Any type' },
        { value: 'warn', label: 'Warns' },
        { value: 'analyse', label: 'Analyse Warns' },
        { value: 'jail', label: 'Admin Jails' },
        { value: 'anticheatJail', label: 'Anticheat Jails' },
        { value: 'ban', label: 'Bans' },
        { value: 'anticheat', label: 'Anticheat Bans' },
    ];
    const selectedTypeLabel = typeFilterOptions
        .find((option) => option.value === typeFilter)?.label ?? 'Any type';
    const selectedAdminLabel = adminNameFilter === SEARCH_ANY_STRING
        ? 'By any admin'
        : adminNameFilter;

    const clearFiltersItem = hasAnyFilter ? (
        <>
            <DropdownMenuSeparator />
            <DropdownMenuItem className="cursor-pointer" onClick={clearAllFilters}>
                <FilterXIcon className="mr-2 h-4 w-4" />
                Clear Filters
            </DropdownMenuItem>
        </>
    ) : null;

    return (
        <div className="p-4 mb-2 md:mb-4 md:rounded-hud border-edge bg-card text-card-foreground shadow-surface">
            <div className="flex flex-wrap-reverse gap-2">
                <div className='relative min-w-44 grow'>
                    <Input
                        type="text"
                        autoFocus
                        autoCapitalize='off'
                        autoCorrect='off'
                        ref={inputRef}
                        placeholder={selectedSearchType.placeholder}
                        defaultValue={initialState.search.value}
                        onKeyDown={handleInputKeyDown}
                    />
                    {hasSearchText ? (
                        <HoverTip label="Clear search">
                            <button
                                type="button"
                                aria-label="Clear search"
                                className={cn(
                                    'absolute right-2 top-1/2 -translate-y-1/2',
                                    'grid place-items-center size-5',
                                    'text-faint transition-colors hover:text-accent',
                                )}
                                onClick={clearSearchBtn}
                            >
                                <XIcon className="size-5" />
                            </button>
                        </HoverTip>
                    ) : (
                        <div className="absolute right-2 inset-y-0 flex items-center text-zinc-400 select-none pointer-events-none">
                            <InlineCode className="text-xs tracking-wide">ctrl+f</InlineCode>
                        </div>
                    )}
                </div>

                <div className="grow flex content-start gap-2 flex-wrap">
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <Button
                                variant="outline"
                                role="combobox"
                                aria-expanded={isSearchTypeDropdownOpen}
                                onClick={() => setSearchTypeDropdownOpen(!isSearchTypeDropdownOpen)}
                                className="xs:w-48 justify-between border-input bg-black/30 hover:border-edge-strong grow md:grow-0"
                            >
                                Search by {selectedSearchType.label}
                                <ChevronsUpDownIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent className='w-48'>
                            <DropdownMenuLabel>Search Type</DropdownMenuLabel>
                            <DropdownMenuSeparator />
                            <DropdownMenuRadioGroup value={currSearchType} onValueChange={setCurrSearchType}>
                                {availableSearchTypes.map((searchType) => (
                                    <DropdownMenuRadioItem
                                        key={searchType.value}
                                        value={searchType.value}
                                        className='cursor-pointer'
                                    >
                                        {searchType.label}
                                    </DropdownMenuRadioItem>
                                ))}
                            </DropdownMenuRadioGroup>
                            <DropdownMenuSeparator />
                            <DropdownMenuCheckboxItem
                                checked={rememberSearchType}
                                className="cursor-pointer"
                                onCheckedChange={setRememberSearchType}
                                //independent of the type above, so the menu stays open
                                onSelect={(event) => event.preventDefault()}
                            >
                                Remember Option
                            </DropdownMenuCheckboxItem>
                        </DropdownMenuContent>
                    </DropdownMenu>


                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <Button
                                variant="outline"
                                role="combobox"
                                className="w-36 justify-between border-input bg-black/30 hover:border-edge-strong grow md:grow-0"
                            >
                                <span className="truncate-flex">{selectedTypeLabel}</span>
                                <ChevronsUpDownIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent className="w-36">
                            <DropdownMenuLabel>Action Type</DropdownMenuLabel>
                            <DropdownMenuSeparator />
                            <DropdownMenuRadioGroup value={typeFilter} onValueChange={setTypeFilter}>
                                {typeFilterOptions.map((option) => (
                                    <DropdownMenuRadioItem
                                        key={option.value}
                                        value={option.value}
                                        className="cursor-pointer"
                                    >
                                        {option.label}
                                    </DropdownMenuRadioItem>
                                ))}
                            </DropdownMenuRadioGroup>
                            {clearFiltersItem}
                        </DropdownMenuContent>
                    </DropdownMenu>

                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <Button
                                variant="outline"
                                role="combobox"
                                className="w-36 justify-between border-input bg-black/30 hover:border-edge-strong grow md:grow-0"
                            >
                                <span className="truncate-flex">{selectedAdminLabel}</span>
                                <ChevronsUpDownIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent className="w-48 max-h-72 overflow-y-auto">
                            <DropdownMenuLabel>Admin</DropdownMenuLabel>
                            <DropdownMenuSeparator />
                            <DropdownMenuRadioGroup value={adminNameFilter} onValueChange={setAdminNameFilter}>
                                <DropdownMenuRadioItem value={SEARCH_ANY_STRING} className="cursor-pointer">
                                    By any admin
                                </DropdownMenuRadioItem>
                                <DropdownMenuRadioItem value={authData.name} className="cursor-pointer">
                                    {authData.name} <span className="opacity-50">({selfActionCount})</span>
                                </DropdownMenuRadioItem>
                                <DropdownMenuSeparator />
                                {filteredAdmins.map((admin) => (
                                    <DropdownMenuRadioItem
                                        key={admin.name}
                                        value={admin.name}
                                        className="cursor-pointer"
                                    >
                                        {admin.name} <span className="opacity-50">({admin.actions})</span>
                                    </DropdownMenuRadioItem>
                                ))}
                            </DropdownMenuRadioGroup>
                            {clearFiltersItem}
                        </DropdownMenuContent>
                    </DropdownMenu>

                    <div className="flex justify-end flex-grow">
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <Button
                                    variant="outline"
                                    className="justify-between border-input bg-black/30 hover:border-edge-strong grow md:grow-0"
                                >
                                    More
                                    <ChevronDownIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent>
                                <DropdownMenuItem className="h-10 pl-1 pr-2 py-2" asChild>
                                    <Link href="/system/master-actions#cleandb" className="cursor-pointer">
                                        <ExternalLinkIcon className="inline mr-1 h-4" />
                                        Bulk Remove
                                    </Link>
                                </DropdownMenuItem>
                                <DropdownMenuItem className="h-10 pl-1 pr-2 py-2" asChild>
                                    <Link href="/settings/ban-templates" className="cursor-pointer">
                                        <ExternalLinkIcon className="inline mr-1 h-4" />
                                        Ban Templates
                                    </Link>
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                    </div>
                </div>
            </div>
            <div className="text-xs text-muted-foreground mt-1 px-1">
                {selectedSearchType.description}
            </div>
        </div>
    )
}
